import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './PricingPageNew.css';

function PricingPageNew({ user, onLogout }) {
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const navigate = useNavigate();

  const handleSubscribe = async (tier) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    setLoadingTier(tier);
    setError('');

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
          tier: tier,
          interval: billingInterval,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoadingTier(null);
    }
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/checker');
    } else {
      navigate('/signup');
    }
  };

  const prices = {
    basic: { monthly: 39, annual: 390 },
    pro: { monthly: 79, annual: 790 }
  };

  const currentPrice = (tier) => prices[tier][billingInterval];
  const monthlySavings = (tier) => {
    const monthlyTotal = prices[tier].monthly * 12;
    const annualTotal = prices[tier].annual;
    return monthlyTotal - annualTotal;
  };

  const faqs = [
    {
      question: 'Can I cancel anytime?',
      answer: "Yes, you can cancel your subscription at any time from your settings page. You'll retain access until the end of your billing period."
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit and debit cards through Stripe.'
    },
    {
      question: 'Is my payment information secure?',
      answer: 'Yes, all payments are processed securely through Stripe. We never store your card details on our servers.'
    },
    {
      question: 'How do monthly check limits work?',
      answer: 'Your check count resets each billing period. A single URL submission counts as one check, even if it crawls multiple pages. Unused checks do not roll over.'
    },
    {
      question: 'What happens if I hit my check limit?',
      answer: "You'll be prompted to upgrade to a higher plan. Your existing check history and results remain accessible."
    },
    {
      question: 'Can I switch between monthly and annual billing?',
      answer: 'Yes, you can switch billing intervals from your settings page. Changes take effect at the start of your next billing period.'
    },
    {
      question: 'What happens to my documents if I downgrade?',
      answer: "Your existing documents remain accessible — you can still view, download, and delete them. You just won't be able to upload new ones until you're within your new plan's document limit."
    }
  ];

  return (
    <div className="pricing-page-new">
      <Navbar user={user} onLogout={onLogout} />

      <div className="pricing-container-new">
        <div className="pricing-header-new">
          <h1>Simple, transparent pricing</h1>
          <p>Choose the plan that works for you</p>

          {/* Billing interval toggle */}
          <div className="billing-toggle">
            <span className={billingInterval === 'monthly' ? 'active' : ''}>Monthly</span>
            <button
              className="toggle-switch"
              onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'annual' : 'monthly')}
              aria-label="Toggle billing interval"
            >
              <span className={`toggle-knob ${billingInterval === 'annual' ? 'annual' : ''}`} />
            </button>
            <span className={billingInterval === 'annual' ? 'active' : ''}>
              Annual
              <span className="save-badge">Save up to 17%</span>
            </span>
          </div>
        </div>

        {error && (
          <div className="pricing-error-new">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        <div className="pricing-cards-new three-tier">
          {/* Free Plan */}
          <div className="pricing-card-new">
            <h3 className="plan-name">Free</h3>
            <p className="plan-desc">Try Mindmark</p>

            <div className="plan-price">
              <span className="currency">A$</span>
              <span className="amount">0</span>
              <span className="period">/ month</span>
            </div>

            <button className="btn-secondary btn-full" onClick={handleGetStarted}>
              Get Started
            </button>

            <ul className="plan-features">
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                3 checks per month
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Website & document analysis
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Basic compliance reports
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>3</strong> document storage <span className="feature-new-badge">New</span>
              </li>
              <li className="feature-disabled">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                No Compliance Chat
              </li>
            </ul>
          </div>

          {/* Basic Plan */}
          <div className="pricing-card-new featured">
            <div className="featured-badge">Most Popular</div>
            <h3 className="plan-name">Basic</h3>
            <p className="plan-desc">Daily compliance support</p>

            <div className="plan-price">
              <span className="currency">A$</span>
              <span className="amount">{currentPrice('basic')}</span>
              <span className="period">/ {billingInterval === 'annual' ? 'year' : 'month'}</span>
            </div>
            {billingInterval === 'annual' && (
              <p className="annual-savings">Save A${monthlySavings('basic')}/year</p>
            )}

            <button
              className="btn-primary btn-full"
              onClick={() => handleSubscribe('basic')}
              disabled={loadingTier === 'basic'}
            >
              {loadingTier === 'basic' ? 'Loading...' : 'Get Basic'}
            </button>

            <p className="plan-includes">Everything in Free, plus:</p>

            <ul className="plan-features">
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>50 checks</strong> per month
              </li>
              <li className="feature-highlight">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>Compliance Chat</strong> - Ask anything
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                AI answers with regulation citations
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                PDF report downloads
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Full check history
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>50</strong> document storage <span className="feature-new-badge">New</span>
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Cancel anytime
              </li>
            </ul>

            <p className="plan-note">Secure payment via Stripe</p>
          </div>

          {/* Pro Plan */}
          <div className="pricing-card-new">
            <h3 className="plan-name">Pro</h3>
            <p className="plan-desc">Unlimited compliance support</p>

            <div className="plan-price">
              <span className="currency">A$</span>
              <span className="amount">{currentPrice('pro')}</span>
              <span className="period">/ {billingInterval === 'annual' ? 'year' : 'month'}</span>
            </div>
            {billingInterval === 'annual' && (
              <p className="annual-savings">Save A${monthlySavings('pro')}/year</p>
            )}

            <button
              className="btn-primary btn-full"
              onClick={() => handleSubscribe('pro')}
              disabled={loadingTier === 'pro'}
            >
              {loadingTier === 'pro' ? 'Loading...' : 'Get Pro'}
            </button>

            <p className="plan-includes">Everything in Basic, plus:</p>

            <ul className="plan-features">
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>Unlimited</strong> compliance checks
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>Unlimited</strong> Compliance Chat
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <strong>200</strong> document storage <span className="feature-new-badge">New</span>
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Priority support
              </li>
            </ul>

            <p className="plan-note">Secure payment via Stripe</p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="pricing-faq">
          <h2>Frequently Asked Questions</h2>

          <div className="faq-list-new">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item-new ${openFaq === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question-new"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points={openFaq === index ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
                  </svg>
                </button>
                {openFaq === index && (
                  <p className="faq-answer-new">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PricingPageNew;
